import { useCallback, useEffect, useState } from "react";
import { Alert, Badge, Button, Card, Col, Container, Form, Nav, Row } from "react-bootstrap";
import { Link, useNavigate, useParams } from "react-router-dom";
import api, { mediaUrl } from "../../api/axios";

const dayOptions = [
  ["monday", "Lunes"],
  ["tuesday", "Martes"],
  ["wednesday", "Miercoles"],
  ["thursday", "Jueves"],
  ["friday", "Viernes"],
  ["saturday", "Sabado"],
  ["sunday", "Domingo"]
];

const defaultOpeningHours = () => dayOptions.reduce((days, [key]) => ({
  ...days,
  [key]: { open: "", close: "", closed: key === "sunday" }
}), {});

const playerStatusLabels = {
  waitlisted: "Lista de espera",
  registered: "Inscrito",
  checked_in: "Confirmado",
  dropped: "Baja"
};

const recommendedRounds = (format, capacity) => {
  const players = Number(capacity || 0);
  if (!players) return 3;
  if (format === "round_robin") return Math.max(1, players - 1);
  if (format === "single_elimination") return Math.max(1, Math.ceil(Math.log2(players)));
  if (players <= 8) return 3;
  if (players <= 16) return 4;
  if (players <= 32) return 5;
  return Math.max(6, Math.ceil(Math.log2(players)));
};

const emptyTournamentForm = {
  name: "",
  startsAt: "",
  capacity: "",
  entryFee: "",
  externalRankingUrl: "",
  description: "",
  roundDuration: "50",
  tableCount: "",
  format: "swiss",
  maxRounds: "3",
  bestOf: "3",
  status: "scheduled",
  notifyChange: false,
  changeMessage: ""
};

function AdminLayout() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [hub, setHub] = useState({ products: [], tournaments: [], posts: [] });
  const [productInquiries, setProductInquiries] = useState([]);
  const [metrics, setMetrics] = useState(null);
  const [status, setStatus] = useState("");
  const [loadError, setLoadError] = useState("");
  const [activeAdminSection, setActiveAdminSection] = useState("profile");
  const [locationQuery, setLocationQuery] = useState("");
  const [locationResults, setLocationResults] = useState([]);
  const [locationStatus, setLocationStatus] = useState("");
  const [selectedTournamentId, setSelectedTournamentId] = useState("");
  const [editingTournamentId, setEditingTournamentId] = useState("");
  const [tournamentDetail, setTournamentDetail] = useState(null);
  const [playerLookup, setPlayerLookup] = useState("");
  const [pendingMatchAction, setPendingMatchAction] = useState("");
  const [profileForm, setProfileForm] = useState({
    name: "",
    slug: "",
    description: "",
    logoUrl: "",
    coverUrl: "",
    phone: "",
    email: "",
    websiteUrl: "",
    instagramUrl: "",
    facebookUrl: "",
    tiktokUrl: "",
    xUrl: "",
    discordUrl: "",
    whatsappUrl: "",
    openingHours: defaultOpeningHours(),
    addressLine: "",
    city: "",
    region: "",
    country: "",
    postalCode: "",
    latitude: "",
    longitude: ""
  });
  const [productForm, setProductForm] = useState({
    name: "",
    productType: "single",
    price: "",
    quantity: "1",
    description: "",
    images: []
  });
  const [tournamentForm, setTournamentForm] = useState(emptyTournamentForm);
  const [tradeDayForm, setTradeDayForm] = useState({ name: "", startsAt: "", endsAt: "", capacity: "", status: "open", description: "" });
  const [communicationBody, setCommunicationBody] = useState("");

  const loadHub = useCallback(() => {
    setLoadError("");
    api
      .get(`/businesses/${slug}/hub`)
      .then(({ data }) => {
        setHub(data);
        setProfileForm({
          name: data.business?.name || "",
          slug: data.business?.slug || "",
          description: data.business?.description || "",
          logoUrl: data.business?.logo_url || "",
          coverUrl: data.business?.cover_url || "",
          phone: data.business?.phone || "",
          email: data.business?.email || "",
          websiteUrl: data.business?.website_url || "",
          instagramUrl: data.business?.instagram_url || "",
          facebookUrl: data.business?.social_links?.facebook || "",
          tiktokUrl: data.business?.social_links?.tiktok || "",
          xUrl: data.business?.social_links?.x || "",
          discordUrl: data.business?.social_links?.discord || "",
          whatsappUrl: data.business?.social_links?.whatsapp || "",
          openingHours: { ...defaultOpeningHours(), ...(data.business?.opening_hours?.days || {}) },
          addressLine: data.business?.address_line || "",
          city: data.business?.city || "",
          region: data.business?.region || "",
          country: data.business?.country || "",
          postalCode: data.business?.postal_code || "",
          latitude: data.business?.latitude || "",
          longitude: data.business?.longitude || ""
        });
        if (!selectedTournamentId && data.tournaments?.[0]?.id) {
          setSelectedTournamentId(data.tournaments[0].id);
        }
      })
      .catch(error => {
        setLoadError(error?.response?.status === 404 ? "La tienda no existe todavia." : "No se pudo cargar la tienda.");
      });
    api
      .get(`/businesses/${slug}/product-inquiries`)
      .then(({ data }) => setProductInquiries(data || []))
      .catch(() => setProductInquiries([]));
  }, [slug, selectedTournamentId]);

  useEffect(() => {
    loadHub();
    api.get("/analytics/platform").then(({ data }) => setMetrics(data)).catch(() => setMetrics(null));
  }, [loadHub]);

  const centsFromInput = value => {
    if (value === "" || value === null || value === undefined) return null;
    return Math.round(Number(value) * 100);
  };

  const moneyFromCents = value => {
    if (value === "" || value === null || value === undefined) return "";
    return (Number(value) / 100).toFixed(2);
  };

  const toDatetimeLocalValue = value => {
    if (!value) return "";
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return "";
    const local = new Date(date.getTime() - date.getTimezoneOffset() * 60000);
    return local.toISOString().slice(0, 16);
  };

  const datetimeLocalToIso = value => {
    if (!value) return null;
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? null : date.toISOString();
  };

  const createCurrentStore = () => {
    setStatus("");
    api
      .post("/businesses", {
        name: slug.replace(/-/g, " "),
        slug,
        description: "Comunidad local Cardora"
      })
      .then(() => {
        setStatus("Tienda creada.");
        loadHub();
      })
      .catch(() => setStatus("No se pudo crear la tienda."));
  };

  const loadTournamentDetail = useCallback(tournamentId => {
    if (!tournamentId) {
      setTournamentDetail(null);
      return;
    }

    api
      .get(`/businesses/${slug}/tournaments/${tournamentId}/manage`)
      .then(({ data }) => setTournamentDetail(data))
      .catch(() => setTournamentDetail(null));
  }, [slug]);

  useEffect(() => {
    loadTournamentDetail(selectedTournamentId);
  }, [selectedTournamentId, loadTournamentDetail]);

  const submitProduct = async event => {
    event.preventDefault();
    setStatus("");
    try {
      const { data: product } = await api.post(`/businesses/${slug}/products`, {
        name: productForm.name,
        productType: productForm.productType,
        description: productForm.description,
        priceCents: centsFromInput(productForm.price),
        quantity: Number(productForm.quantity || 0),
        status: "available"
      });
      if (productForm.images.length > 0) {
        const formData = new FormData();
        productForm.images.forEach(image => formData.append("images", image));
        await api.post(`/businesses/${slug}/products/${product.id}/images`, formData, {
          headers: { "Content-Type": "multipart/form-data" }
        });
      }
      setStatus("Producto publicado.");
      setProductForm({ name: "", productType: "single", price: "", quantity: "1", description: "", images: [] });
      loadHub();
    } catch (error) {
      setStatus("No se pudo publicar el producto.");
    }
  };

  const submitProfile = event => {
    event.preventDefault();
    setStatus("");
    const socialLinks = {
      facebook: profileForm.facebookUrl,
      tiktok: profileForm.tiktokUrl,
      x: profileForm.xUrl,
      discord: profileForm.discordUrl,
      whatsapp: profileForm.whatsappUrl
    };
    api
      .patch(`/businesses/${slug}/profile`, {
        ...profileForm,
        latitude: profileForm.latitude === "" ? null : Number(profileForm.latitude),
        longitude: profileForm.longitude === "" ? null : Number(profileForm.longitude),
        socialLinks,
        openingHours: { days: profileForm.openingHours }
      })
      .then(({ data }) => {
        setStatus("Perfil de tienda actualizado.");
        if (data.slug && data.slug !== slug) {
          navigate(`/admin/${data.slug}`, { replace: true });
        } else {
          loadHub();
        }
      })
      .catch(error => setStatus(error?.response?.data?.error || "No se pudo actualizar el perfil de tienda."));
  };

  const updateProductInquiry = async (inquiryId, nextStatus) => {
    await api.patch(`/businesses/${slug}/product-inquiries/${inquiryId}`, { status: nextStatus });
    const { data } = await api.get(`/businesses/${slug}/product-inquiries`);
    setProductInquiries(data || []);
    setStatus("Consulta de producto actualizada.");
  };

  const uploadStoreMedia = (mediaType, file) => {
    if (!file) return;
    setStatus("");
    const formData = new FormData();
    formData.append("image", file);
    api
      .post(`/businesses/${slug}/${mediaType}`, formData)
      .then(({ data }) => {
        setStatus(mediaType === "logo" ? "Logo actualizado." : "Banner actualizado.");
        setHub(current => ({ ...current, business: data }));
        setProfileForm(current => ({
          ...current,
          logoUrl: data.logo_url || current.logoUrl,
          coverUrl: data.cover_url || current.coverUrl
        }));
      })
      .catch(() => setStatus("No se pudo subir la imagen."));
  };

  const searchLocation = () => {
    const query = locationQuery.trim() || [profileForm.addressLine, profileForm.city, profileForm.country].filter(Boolean).join(", ");
    if (!query) {
      setLocationStatus("Completa una direccion o escribe una busqueda.");
      return;
    }

    setLocationStatus("Buscando ubicacion...");
    fetch(`https://nominatim.openstreetmap.org/search?format=json&limit=5&q=${encodeURIComponent(query)}`)
      .then(response => response.json())
      .then(results => {
        setLocationResults(results || []);
        setLocationStatus(results?.length ? "" : "No encontramos resultados para esa busqueda.");
      })
      .catch(() => setLocationStatus("No se pudo buscar la ubicacion."));
  };

  const selectLocation = result => {
    const parts = result.display_name?.split(",").map(part => part.trim()) || [];
    setProfileForm(current => ({
      ...current,
      addressLine: current.addressLine || parts.slice(0, 2).join(", "),
      city: current.city || parts[2] || "",
      country: current.country || parts[parts.length - 1] || "",
      latitude: result.lat || "",
      longitude: result.lon || ""
    }));
    setLocationResults([]);
    setLocationQuery(result.display_name || "");
    setLocationStatus("Ubicacion seleccionada. Guarda el perfil para publicarla.");
  };

  const adminMapLat = Number(profileForm.latitude);
  const adminMapLon = Number(profileForm.longitude);
  const adminMapDelta = 0.004;
  const adminMapEmbedSrc = profileForm.latitude && profileForm.longitude
    ? `https://www.openstreetmap.org/export/embed.html?bbox=${adminMapLon - adminMapDelta},${adminMapLat - adminMapDelta},${adminMapLon + adminMapDelta},${adminMapLat + adminMapDelta}&layer=mapnik&marker=${adminMapLat},${adminMapLon}`
    : "";

  const updateOpeningHour = (dayKey, field, value) => {
    setProfileForm(current => ({
      ...current,
      openingHours: {
        ...current.openingHours,
        [dayKey]: {
          ...current.openingHours[dayKey],
          [field]: value,
          ...(field === "closed" && value ? { open: "", close: "" } : {})
        }
      }
    }));
  };


  const submitTradeDay = event => {
    event.preventDefault();
    setStatus("");
    api
      .post(`/businesses/${slug}/trade-days`, {
        name: tradeDayForm.name,
        description: tradeDayForm.description,
        startsAt: datetimeLocalToIso(tradeDayForm.startsAt),
        endsAt: datetimeLocalToIso(tradeDayForm.endsAt),
        capacity: tradeDayForm.capacity ? Number(tradeDayForm.capacity) : null,
        status: tradeDayForm.status
      })
      .then(() => {
        setStatus("Trade Day publicado.");
        setTradeDayForm({ name: "", startsAt: "", endsAt: "", capacity: "", status: "open", description: "" });
        loadHub();
      })
      .catch(error => setStatus(error?.response?.data?.error || "No se pudo publicar el Trade Day."));
  };
  const submitTournament = event => {
    event.preventDefault();
    setStatus("");
    const payload = {
      name: tournamentForm.name,
      description: tournamentForm.description,
      startsAt: datetimeLocalToIso(tournamentForm.startsAt),
      capacity: tournamentForm.capacity ? Number(tournamentForm.capacity) : null,
      entryFeeCents: centsFromInput(tournamentForm.entryFee),
      externalRankingUrl: tournamentForm.externalRankingUrl,
      roundDurationMinutes: tournamentForm.roundDuration ? Number(tournamentForm.roundDuration) : 50,
      tableCount: tournamentForm.tableCount ? Number(tournamentForm.tableCount) : null,
      format: tournamentForm.format,
      maxRounds: tournamentForm.maxRounds ? Number(tournamentForm.maxRounds) : null,
      bestOf: Number(tournamentForm.bestOf || 3),
      status: tournamentForm.status,
      notifyChange: editingTournamentId ? Boolean(tournamentForm.notifyChange) : false,
      changeMessage: editingTournamentId ? tournamentForm.changeMessage : ""
    };

    api
      [editingTournamentId ? "patch" : "post"](
        editingTournamentId
          ? `/businesses/${slug}/tournaments/${editingTournamentId}`
          : `/businesses/${slug}/tournaments`,
        payload
      )
      .then(() => {
        setStatus(editingTournamentId ? "Evento actualizado." : "Evento publicado.");
        setTournamentForm(emptyTournamentForm);
        setEditingTournamentId("");
        loadHub();
        if (selectedTournamentId) loadTournamentDetail(selectedTournamentId);
      })
      .catch(() => setStatus(editingTournamentId ? "No se pudo actualizar el evento." : "No se pudo publicar el evento."));
  };

  const editTournament = tournament => {
    setEditingTournamentId(tournament.id);
    setSelectedTournamentId(tournament.id);
    setTournamentForm({
      name: tournament.name || "",
      startsAt: toDatetimeLocalValue(tournament.starts_at),
      capacity: tournament.capacity || "",
      entryFee: moneyFromCents(tournament.entry_fee_cents),
      externalRankingUrl: tournament.external_ranking_url || "",
      description: tournament.description || "",
      roundDuration: String(tournament.round_duration_minutes || 50),
      tableCount: tournament.table_count || "",
      format: tournament.format || "swiss",
      maxRounds: tournament.max_rounds || "",
      bestOf: String(tournament.best_of || 3),
      status: tournament.status || "scheduled",
      notifyChange: false,
      changeMessage: ""
    });
  };

  const resetTournamentForm = () => {
    setEditingTournamentId("");
    setTournamentForm(emptyTournamentForm);
  };

  const generateRound = () => {
    if (!selectedTournamentId) return;
    setStatus("");
    api
      .post(`/businesses/${slug}/tournaments/${selectedTournamentId}/rounds/generate`)
      .then(() => {
        setStatus("Ronda generada.");
        loadTournamentDetail(selectedTournamentId);
        loadHub();
      })
      .catch(error => setStatus(error?.response?.data?.error || "No se pudo generar la ronda."));
  };

  const finalizeTournament = () => {
    if (!selectedTournamentId) return;
    setStatus("");
    api
      .post(`/businesses/${slug}/tournaments/${selectedTournamentId}/finalize`)
      .then(() => {
        setStatus("Evento finalizado y resultados publicados.");
        loadTournamentDetail(selectedTournamentId);
        loadHub();
      })
      .catch(error => setStatus(error?.response?.data?.error || "No se pudo finalizar el evento."));
  };

  const markPlayerStatus = (userId, nextStatus) => {
    api
      .patch(`/businesses/${slug}/tournaments/${selectedTournamentId}/players/${userId}`, { status: nextStatus })
      .then(() => loadTournamentDetail(selectedTournamentId))
      .catch(() => setStatus("No se pudo actualizar el jugador."));
  };

  const addPlayer = event => {
    event.preventDefault();
    if (!selectedTournamentId || !playerLookup.trim()) return;

    api
      .post(`/businesses/${slug}/tournaments/${selectedTournamentId}/players`, {
        userIdOrEmail: playerLookup.trim()
      })
      .then(() => {
        setPlayerLookup("");
        setStatus("Jugador inscrito.");
        loadTournamentDetail(selectedTournamentId);
        loadHub();
      })
      .catch(error => setStatus(error?.response?.data?.error || "No se pudo inscribir el jugador."));
  };

  const recordGame = (match, winnerId) => {
    if (pendingMatchAction) return;
    setPendingMatchAction(match.id);
    api
      .post(`/businesses/${slug}/tournaments/${selectedTournamentId}/matches/${match.id}/games`, {
        winnerId
      })
      .then(result => {
        setStatus(
          result.data.decided
            ? `Serie finalizada ${result.data.player_one_score}-${result.data.player_two_score}.`
            : `Partida ${result.data.game_number} guardada.`
        );
        loadTournamentDetail(selectedTournamentId);
        loadHub();
      })
      .catch(error => setStatus(error?.response?.data?.error || "No se pudo guardar la partida."))
      .finally(() => setPendingMatchAction(""));
  };

  const undoLastGame = match => {
    if (pendingMatchAction) return;
    setPendingMatchAction(match.id);
    api
      .delete(`/businesses/${slug}/tournaments/${selectedTournamentId}/matches/${match.id}/games/last`)
      .then(() => {
        setStatus("Ultima partida corregida.");
        loadTournamentDetail(selectedTournamentId);
        loadHub();
      })
      .catch(error => setStatus(error?.response?.data?.error || "No se pudo corregir la partida."))
      .finally(() => setPendingMatchAction(""));
  };

  const recordResult = (match, resultType, preserveScore = false) => {
    if (pendingMatchAction) return;
    setPendingMatchAction(match.id);
    let winnerId = null;
    let playerOneScore = preserveScore ? Number(match.player_one_score || 0) : 0;
    let playerTwoScore = preserveScore ? Number(match.player_two_score || 0) : 0;
    const winsNeeded = Math.floor(Number(tournamentDetail?.tournament?.best_of || 3) / 2) + 1;

    if (resultType === "player_one_win") {
      winnerId = match.player_one_id;
      if (!preserveScore) playerOneScore = winsNeeded;
    } else if (resultType === "player_two_win") {
      winnerId = match.player_two_id;
      if (!preserveScore) playerTwoScore = winsNeeded;
    } else if (resultType === "draw") {
      playerOneScore = 1;
      playerTwoScore = 1;
    } else if (resultType === "bye") {
      winnerId = match.player_one_id;
      playerOneScore = winsNeeded;
    }

    api
      .patch(`/businesses/${slug}/tournaments/${selectedTournamentId}/matches/${match.id}/result`, {
        winnerId,
        playerOneScore,
        playerTwoScore,
        resultType
      })
      .then(() => {
        loadTournamentDetail(selectedTournamentId);
        loadHub();
      })
      .catch(() => setStatus("No se pudo guardar el resultado."))
      .finally(() => setPendingMatchAction(""));
  };

  const submitCommunication = event => {
    event.preventDefault();
    setStatus("");
    api
      .post(`/businesses/${slug}/communications`, {
        body: communicationBody,
        type: "store_post"
      })
      .then(() => {
        setStatus("Comunicado publicado.");
        setCommunicationBody("");
        loadHub();
      })
      .catch(() => setStatus("No se pudo publicar el comunicado."));
  };


  const tournamentControl = (() => {
    if (!tournamentDetail) {
      return {
        activePlayers: [],
        checkedInPlayers: [],
        waitlistedPlayers: [],
        currentRound: null,
        pendingMatches: 0,
        completedMatches: 0,
        totalMatches: 0,
        nextRoundNumber: 1,
        maxRounds: null,
        maxRoundsReached: false,
        canGenerateRound: false,
        canFinalize: false,
        progress: 0,
        nextAction: selectedTournamentId ? "Cargando evento..." : "Selecciona un evento para gestionarlo."
      };
    }

    const rounds = tournamentDetail.rounds || [];
    const currentRound = rounds[rounds.length - 1] || null;
    const matches = currentRound?.matches || [];
    const pendingMatches = matches.filter(match => match.status !== "completed").length;
    const completedMatches = matches.filter(match => match.status === "completed").length;
    const totalMatches = matches.length;
    const activePlayers = (tournamentDetail.players || []).filter(player => !["dropped", "waitlisted"].includes(player.status));
    const checkedInPlayers = (tournamentDetail.players || []).filter(player => player.status === "checked_in");
    const waitlistedPlayers = (tournamentDetail.players || []).filter(player => player.status === "waitlisted");
    const nextRoundNumber = rounds.length + 1;
    const maxRounds = Number(tournamentDetail.tournament?.max_rounds || 0) || null;
    const tableCount = Number(tournamentDetail.tournament?.table_count || 0) || null;
    const requiredTables = Math.floor(activePlayers.length / 2);
    const hasEnoughTables = !tableCount || requiredTables <= tableCount;
    const maxRoundsReached = Boolean(maxRounds && nextRoundNumber > maxRounds);
    const tournamentClosed = ["completed", "cancelled"].includes(tournamentDetail.tournament?.status);
    const hasPendingRound = Boolean(currentRound && pendingMatches > 0);
    const canGenerateRound = Boolean(
      selectedTournamentId &&
      !tournamentClosed &&
      activePlayers.length >= 2 &&
      !hasPendingRound &&
      !maxRoundsReached &&
      hasEnoughTables
    );
    const canFinalize = Boolean(
      selectedTournamentId &&
      !tournamentClosed &&
      rounds.length > 0 &&
      pendingMatches === 0
    );

    let nextAction = "Genera la primera ronda cuando tengas los jugadores confirmados.";
    if (tournamentClosed) nextAction = "Evento cerrado.";
    else if (activePlayers.length < 2) nextAction = "Necesitas al menos 2 jugadores activos para generar ronda.";
    else if (hasPendingRound) nextAction = "Completa las mesas pendientes antes de generar otra ronda.";
    else if (!hasEnoughTables) nextAction = `Necesitas ${requiredTables} mesas para esta ronda y hay ${tableCount} configuradas.`;
    else if (maxRoundsReached) nextAction = "Se alcanzo el maximo de rondas. Ya puedes finalizar el evento.";
    else if (rounds.length > 0) nextAction = "Ronda completa. Puedes generar la siguiente o finalizar si corresponde.";

    return {
      activePlayers,
      checkedInPlayers,
      waitlistedPlayers,
      currentRound,
      pendingMatches,
      completedMatches,
      totalMatches,
      tableCount,
      requiredTables,
      hasEnoughTables,
      nextRoundNumber,
      maxRounds,
      maxRoundsReached,
      canGenerateRound,
      canFinalize,
      progress: totalMatches ? Math.round((completedMatches / totalMatches) * 100) : 0,
      nextAction
    };
  })();

  return (
    <Container fluid className="page-band px-4 store-page">
      <div className="mb-4">
        <h1 className="section-title mb-1">Admin tienda</h1>
        <p className="muted-copy mb-0">{hub.business?.name || slug}</p>
      </div>
      {loadError && (
        <Alert variant="warning" className="d-flex flex-column flex-md-row justify-content-between gap-3 align-items-md-center">
          <span>{loadError}</span>
          <Button variant="outline-dark" onClick={createCurrentStore}>
            Crear tienda {slug}
          </Button>
        </Alert>
      )}
      {status && <div className="inline-page-notice mb-3">{status}</div>}

      <div className="admin-dashboard-hero mb-3">
        <div>
          <span className="muted-copy">Panel de gestion</span>
          <h2>{hub.business?.name || slug}</h2>
          <p>{hub.business?.description || "Configura la tienda, publica productos, gestiona eventos y habla con la comunidad."}</p>
        </div>
        <div className="admin-hero-stats">
          <span>{hub.products?.length || 0} productos</span>
          <span>{hub.tournaments?.length || 0} eventos</span>
          <span>{hub.posts?.length || 0} posts</span>
          <Button as={Link} to={`/store/${hub.business?.slug || slug}`} size="sm" variant="outline-light">
            Ver perfil publico
          </Button>
        </div>
      </div>

      <Nav
        variant="tabs"
        activeKey={activeAdminSection}
        onSelect={key => setActiveAdminSection(key || "profile")}
        className="binder-tabs admin-tabs mb-4"
      >
        <Nav.Item>
          <Nav.Link eventKey="profile">Perfil</Nav.Link>
        </Nav.Item>
        <Nav.Item>
          <Nav.Link eventKey="products">Productos</Nav.Link>
        </Nav.Item>
        <Nav.Item>
          <Nav.Link eventKey="tradeDays">Trade Days</Nav.Link>
        </Nav.Item>
        <Nav.Item>
          <Nav.Link eventKey="tournaments">Eventos</Nav.Link>
        </Nav.Item>
        <Nav.Item>
          <Nav.Link eventKey="communications">Comunicados</Nav.Link>
        </Nav.Item>
        <Nav.Item>
          <Nav.Link eventKey="metrics">Metricas</Nav.Link>
        </Nav.Item>
      </Nav>

      {metrics && activeAdminSection === "metrics" && (
        <Row className="g-3 mb-3">
          <Col sm={6} xl={3}>
            <div className="store-metric">
              <span>Registrados</span>
              <strong>{metrics.registered_users}</strong>
            </div>
          </Col>
          <Col sm={6} xl={3}>
            <div className="store-metric">
              <span>Binders completos</span>
              <strong>{metrics.completed_binder_users}</strong>
            </div>
          </Col>
          <Col sm={6} xl={3}>
            <div className="store-metric">
              <span>Vuelven semana 2</span>
              <strong>{metrics.returned_week_2_users}</strong>
            </div>
          </Col>
          <Col sm={6} xl={3}>
            <div className="store-metric">
              <span>Publican</span>
              <strong>{metrics.users_who_posted}</strong>
            </div>
          </Col>
        </Row>
      )}

      {activeAdminSection === "profile" && <Card className="cardora-card mb-3">
        <Card.Body>
          <Card.Title>Perfil de tienda</Card.Title>
          <Form className="admin-form-grid" onSubmit={submitProfile}>
            <div className="admin-profile-sections">
              <section className="admin-profile-section">
                <div className="admin-section-heading">
                  <strong>Identidad publica</strong>
                  <span>Nombre, URL y descripcion principal.</span>
                </div>
                <Row className="g-2">
                  <Col md={6}>
                    <Form.Control
                      placeholder="Nombre"
                      value={profileForm.name}
                      onChange={event => setProfileForm({ ...profileForm, name: event.target.value })}
                    />
                  </Col>
                  <Col md={6}>
                    <Form.Control
                      placeholder="Slug publico"
                      value={profileForm.slug}
                      onChange={event => setProfileForm({ ...profileForm, slug: event.target.value })}
                    />
                  </Col>
                  <Col xs={12}>
                    <Form.Control
                      as="textarea"
                      rows={2}
                      placeholder="Descripcion, idioma, estado, expansion o detalle para la comunidad"
                      value={profileForm.description}
                      onChange={event => setProfileForm({ ...profileForm, description: event.target.value })}
                    />
                  </Col>
                </Row>
              </section>

              <section className="admin-profile-section">
                <div className="admin-section-heading">
                  <strong>Imagenes</strong>
                  <span>Logo circular y banner de portada.</span>
                </div>
                <Row className="g-2">
                  <Col md={5}>
                    <Form.Label>Logo</Form.Label>
                    {profileForm.logoUrl && <img className="admin-media-preview logo" src={mediaUrl(profileForm.logoUrl)} alt="" />}
                    <Form.Control type="file" accept="image/*" onChange={event => uploadStoreMedia("logo", event.target.files?.[0])} />
                  </Col>
                  <Col md={7}>
                    <Form.Label>Banner</Form.Label>
                    {profileForm.coverUrl && <img className="admin-media-preview cover" src={mediaUrl(profileForm.coverUrl)} alt="" />}
                    <Form.Control type="file" accept="image/*" onChange={event => uploadStoreMedia("cover", event.target.files?.[0])} />
                  </Col>
                </Row>
              </section>

              <section className="admin-profile-section">
                <div className="admin-section-heading">
                  <strong>Contacto y redes</strong>
                  <span>Canales visibles para comunidad y clientes.</span>
                </div>
                <Row className="g-2">
                  <Col md={6}>
                    <Form.Control
                      placeholder="Email"
                      value={profileForm.email}
                      onChange={event => setProfileForm({ ...profileForm, email: event.target.value })}
                    />
                  </Col>
                  <Col md={6}>
                    <Form.Control
                      placeholder="Telefono"
                      value={profileForm.phone}
                      onChange={event => setProfileForm({ ...profileForm, phone: event.target.value })}
                    />
                  </Col>
                  <Col md={6}>
                    <Form.Control
                      placeholder="Web"
                      value={profileForm.websiteUrl}
                      onChange={event => setProfileForm({ ...profileForm, websiteUrl: event.target.value })}
                    />
                  </Col>
                  <Col md={6}>
                    <Form.Control
                      placeholder="Instagram"
                      value={profileForm.instagramUrl}
                      onChange={event => setProfileForm({ ...profileForm, instagramUrl: event.target.value })}
                    />
                  </Col>
                  <Col md={4}>
                    <Form.Control
                      placeholder="Facebook"
                      value={profileForm.facebookUrl}
                      onChange={event => setProfileForm({ ...profileForm, facebookUrl: event.target.value })}
                    />
                  </Col>
                  <Col md={4}>
                    <Form.Control
                      placeholder="TikTok"
                      value={profileForm.tiktokUrl}
                      onChange={event => setProfileForm({ ...profileForm, tiktokUrl: event.target.value })}
                    />
                  </Col>
                  <Col md={4}>
                    <Form.Control
                      placeholder="X / Twitter"
                      value={profileForm.xUrl}
                      onChange={event => setProfileForm({ ...profileForm, xUrl: event.target.value })}
                    />
                  </Col>
                  <Col md={6}>
                    <Form.Control
                      placeholder="Discord"
                      value={profileForm.discordUrl}
                      onChange={event => setProfileForm({ ...profileForm, discordUrl: event.target.value })}
                    />
                  </Col>
                  <Col md={6}>
                    <Form.Control
                      placeholder="WhatsApp"
                      value={profileForm.whatsappUrl}
                      onChange={event => setProfileForm({ ...profileForm, whatsappUrl: event.target.value })}
                    />
                  </Col>
                </Row>
              </section>

              <section className="admin-profile-section">
                <div className="admin-section-heading">
                  <strong>Ubicacion</strong>
                  <span>Direccion publica y posicion del mapa.</span>
                </div>
                <Row className="g-2">
                  <Col md={6}>
                    <Form.Control
                      placeholder="Direccion"
                      value={profileForm.addressLine}
                      onChange={event => setProfileForm({ ...profileForm, addressLine: event.target.value })}
                    />
                  </Col>
                  <Col md={3}>
                    <Form.Control
                      placeholder="Ciudad"
                      value={profileForm.city}
                      onChange={event => setProfileForm({ ...profileForm, city: event.target.value })}
                    />
                  </Col>
                  <Col md={3}>
                    <Form.Control
                      placeholder="Pais"
                      value={profileForm.country}
                      onChange={event => setProfileForm({ ...profileForm, country: event.target.value })}
                    />
                  </Col>
                </Row>
                <div className="admin-location-picker">
                  <div className="admin-location-search">
                    <Form.Control
                      placeholder="Buscar ubicacion en OpenStreetMap"
                      value={locationQuery}
                      onChange={event => setLocationQuery(event.target.value)}
                    />
                    <Button type="button" variant="outline-dark" onClick={searchLocation}>
                      Buscar mapa
                    </Button>
                  </div>
                  {locationStatus && <div className="muted-copy">{locationStatus}</div>}
                  {locationResults.length > 0 && (
                    <div className="admin-location-results">
                      {locationResults.map(result => (
                        <button type="button" key={result.place_id} onClick={() => selectLocation(result)}>
                          <strong>{result.name || result.display_name}</strong>
                          <span>{result.display_name}</span>
                        </button>
                      ))}
                    </div>
                  )}
                  {adminMapEmbedSrc && (
                    <div className="admin-map-preview">
                      <iframe title="Ubicacion de tienda" src={adminMapEmbedSrc} loading="lazy" />
                    </div>
                  )}
                </div>
                <Row className="g-2">
                  <Col md={6}>
                    <Form.Control
                      placeholder="Latitud"
                      type="number"
                      step="0.0000001"
                      value={profileForm.latitude}
                      onChange={event => setProfileForm({ ...profileForm, latitude: event.target.value })}
                    />
                  </Col>
                  <Col md={6}>
                    <Form.Control
                      placeholder="Longitud"
                      type="number"
                      step="0.0000001"
                      value={profileForm.longitude}
                      onChange={event => setProfileForm({ ...profileForm, longitude: event.target.value })}
                    />
                  </Col>
                </Row>
              </section>

              <section className="admin-profile-section">
                <div className="admin-section-heading">
                  <strong>Horarios</strong>
                  <span>Apertura, cierre y dias cerrados.</span>
                </div>
                <div className="admin-hours-editor">
                  {dayOptions.map(([dayKey, label]) => {
                    const day = profileForm.openingHours[dayKey] || { open: "", close: "", closed: false };
                    return (
                      <div className="admin-hour-row" key={dayKey}>
                        <span>{label}</span>
                        <Form.Check
                          type="switch"
                          label="Cerrado"
                          checked={Boolean(day.closed)}
                          onChange={event => updateOpeningHour(dayKey, "closed", event.target.checked)}
                        />
                        <Form.Control
                          type="time"
                          value={day.open || ""}
                          disabled={day.closed}
                          onChange={event => updateOpeningHour(dayKey, "open", event.target.value)}
                        />
                        <Form.Control
                          type="time"
                          value={day.close || ""}
                          disabled={day.closed}
                          onChange={event => updateOpeningHour(dayKey, "close", event.target.value)}
                        />
                      </div>
                    );
                  })}
                </div>
              </section>
            </div>
            <Button type="submit" className="btn-cardora">
              Guardar perfil
            </Button>
          </Form>
        </Card.Body>
      </Card>}

      <Row className="g-3">

        {activeAdminSection === "tradeDays" && <Col lg={7}>
          <Card className="cardora-card h-100">
            <Card.Body>
              <Card.Title>Organizar Trade Day</Card.Title>
              <p className="muted-copy">Evento de comunidad para que los jugadores confirmen asistencia, preparen binder/wishlist y Cardora recomiende posibles trades.</p>
              <Form className="admin-form-grid" onSubmit={submitTradeDay}>
                <Form.Control
                  placeholder="Nombre del Trade Day"
                  value={tradeDayForm.name}
                  onChange={event => setTradeDayForm({ ...tradeDayForm, name: event.target.value })}
                  required
                />
                <Row className="g-2">
                  <Col md={6}>
                    <Form.Label>Inicio</Form.Label>
                    <Form.Control type="datetime-local" value={tradeDayForm.startsAt} onChange={event => setTradeDayForm({ ...tradeDayForm, startsAt: event.target.value })} />
                  </Col>
                  <Col md={6}>
                    <Form.Label>Fin</Form.Label>
                    <Form.Control type="datetime-local" value={tradeDayForm.endsAt} onChange={event => setTradeDayForm({ ...tradeDayForm, endsAt: event.target.value })} />
                  </Col>
                </Row>
                <Row className="g-2">
                  <Col md={6}>
                    <Form.Control type="number" min="1" placeholder="Cupo opcional" value={tradeDayForm.capacity} onChange={event => setTradeDayForm({ ...tradeDayForm, capacity: event.target.value })} />
                  </Col>
                  <Col md={6}>
                    <Form.Select value={tradeDayForm.status} onChange={event => setTradeDayForm({ ...tradeDayForm, status: event.target.value })}>
                      <option value="open">Asistencia abierta</option>
                      <option value="scheduled">Programado</option>
                      <option value="draft">Borrador</option>
                    </Form.Select>
                  </Col>
                </Row>
                <Form.Control as="textarea" rows={4} placeholder="Descripcion, dinamica del evento, recomendaciones para llevar cartas..." value={tradeDayForm.description} onChange={event => setTradeDayForm({ ...tradeDayForm, description: event.target.value })} />
                <Button type="submit" className="btn-cardora">Publicar Trade Day</Button>
              </Form>
            </Card.Body>
          </Card>
        </Col>}
        {activeAdminSection === "tradeDays" && <Col lg={5}>
          <Card className="cardora-card h-100">
            <Card.Body>
              <Card.Title>Trade Days publicados</Card.Title>
              <div className="admin-list compact">
                {(hub.tradeDays || []).length === 0 && <p className="muted-copy mb-0">Aun no hay Trade Days.</p>}
                {(hub.tradeDays || []).map(event => (
                  <div className="store-home-event" key={event.id}>
                    <strong>{event.name}</strong>
                    <span>{event.starts_at ? new Date(event.starts_at).toLocaleString("es-ES") : "Fecha por confirmar"}</span>
                    <small>{event.attendees_count || 0} asistentes · {event.status}</small>
                  </div>
                ))}
              </div>
            </Card.Body>
          </Card>
        </Col>}
        {activeAdminSection === "products" && <Col lg={7}>
          <Card className="cardora-card h-100">
            <Card.Body>
              <Card.Title>Producto / catalogo de tienda</Card.Title>
              <Form className="admin-form-grid" onSubmit={submitProduct}>
                <Form.Control
                  placeholder="Nombre"
                  value={productForm.name}
                  onChange={event => setProductForm({ ...productForm, name: event.target.value })}
                  required
                />
                <Form.Select
                  value={productForm.productType}
                  onChange={event => setProductForm({ ...productForm, productType: event.target.value })}
                >
                  <option value="single">Carta suelta</option>
                  <option value="sealed">Sellado</option>
                  <option value="booster">Booster</option>
                  <option value="accessory">Accesorio</option>
                  <option value="service">Servicio</option>
                  <option value="other">Otro</option>
                </Form.Select>
                <Row className="g-2">
                  <Col>
                    <Form.Control
                      type="number"
                      min="0"
                      step="0.01"
                      placeholder="Precio opcional"
                      value={productForm.price}
                      onChange={event => setProductForm({ ...productForm, price: event.target.value })}
                    />
                  </Col>
                  <Col>
                    <Form.Control
                      type="number"
                      min="0"
                      placeholder="Cantidad visible"
                      value={productForm.quantity}
                      onChange={event => setProductForm({ ...productForm, quantity: event.target.value })}
                    />
                  </Col>
                </Row>
                <Form.Group>
                  <Form.Label>Fotos del producto</Form.Label>
                  <Form.Control
                    type="file"
                    accept="image/*"
                    multiple
                    onChange={event => setProductForm({
                      ...productForm,
                      images: Array.from(event.target.files || []).slice(0, 4)
                    })}
                  />
                  <Form.Text>Hasta 4 fotos. Puedes publicar sin precio para mostrar catalogo y recibir consultas.</Form.Text>
                </Form.Group>
                <Form.Control
                  as="textarea"
                  rows={3}
                  placeholder="Descripcion"
                  value={productForm.description}
                  onChange={event => setProductForm({ ...productForm, description: event.target.value })}
                />
                <Button type="submit" className="btn-cardora">
                  Publicar en catalogo
                </Button>
              </Form>
            </Card.Body>
          </Card>
        </Col>}
        {activeAdminSection === "products" && <Col lg={5}>
          <Card className="cardora-card h-100">
            <Card.Body>
              <Card.Title>Catalogo publicado</Card.Title>
              <div className="admin-list">
                {hub.products?.length === 0 && <div className="muted-copy">Todavia no hay productos en catalogo.</div>}
                {hub.products?.map(product => (
                  <div className="admin-resource-row" key={product.id}>
                    <div>
                      <strong>{product.name}</strong>
                      <span>{product.product_type} · {product.quantity} disp.</span>
                    </div>
                    <Badge bg={product.status === "available" ? "success" : "secondary"}>{product.status}</Badge>
                  </div>
                ))}
              </div>
            </Card.Body>
          </Card>
        </Col>}
        {activeAdminSection === "products" && <Col xs={12}>
          <Card className="cardora-card">
            <Card.Body>
              <Card.Title>Consultas y reservas</Card.Title>
              <div className="admin-list">
                {productInquiries.length === 0 && <div className="muted-copy">No hay consultas de productos.</div>}
                {productInquiries.map(inquiry => (
                  <div className="admin-resource-row" key={inquiry.id}>
                    <div>
                      <strong>{inquiry.display_name} · {inquiry.product_name}</strong>
                      <span>{inquiry.quantity} unidad(es) · {inquiry.message || "Sin mensaje"}</span>
                    </div>
                    <div className="d-flex flex-wrap gap-1 align-items-center">
                      <Badge bg={inquiry.status === "completed" ? "success" : "light"} text={inquiry.status === "completed" ? undefined : "dark"}>
                        {inquiry.status}
                      </Badge>
                      {inquiry.status === "pending" && (
                        <Button size="sm" variant="outline-dark" onClick={() => updateProductInquiry(inquiry.id, "contacted")}>
                          Contactado
                        </Button>
                      )}
                      {["pending", "contacted"].includes(inquiry.status) && (
                        <Button size="sm" className="btn-cardora" onClick={() => updateProductInquiry(inquiry.id, "reserved")}>
                          Reservar
                        </Button>
                      )}
                      {inquiry.status === "reserved" && (
                        <Button size="sm" className="btn-cardora" onClick={() => updateProductInquiry(inquiry.id, "completed")}>
                          Completar
                        </Button>
                      )}
                      {!["completed", "declined", "cancelled"].includes(inquiry.status) && (
                        <Button size="sm" variant="outline-danger" onClick={() => updateProductInquiry(inquiry.id, "declined")}>
                          Rechazar
                        </Button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </Card.Body>
          </Card>
        </Col>}

        {activeAdminSection === "tournaments" && <Col lg={7}>
          <Card className="cardora-card h-100">
            <Card.Body>
              <div className="d-flex justify-content-between align-items-start gap-2 mb-2">
                <Card.Title>{editingTournamentId ? "Editar evento" : "Nuevo evento"}</Card.Title>
                {editingTournamentId && (
                  <Button size="sm" variant="outline-dark" onClick={resetTournamentForm}>
                    Nuevo
                  </Button>
                )}
              </div>
              <Form className="admin-form-grid" onSubmit={submitTournament}>
                <Form.Control
                  placeholder="Nombre"
                  value={tournamentForm.name}
                  onChange={event => setTournamentForm({ ...tournamentForm, name: event.target.value })}
                  required
                />
                <Form.Control
                  type="datetime-local"
                  value={tournamentForm.startsAt}
                  onChange={event => setTournamentForm({ ...tournamentForm, startsAt: event.target.value })}
                />
                <Row className="g-2">
                  <Col>
                    <Form.Select
                      value={tournamentForm.format}
                      onChange={event => {
                        const format = event.target.value;
                        setTournamentForm({
                          ...tournamentForm,
                          format,
                          maxRounds: String(recommendedRounds(format, tournamentForm.capacity))
                        });
                      }}
                    >
                      <option value="swiss">Suizo</option>
                      <option value="round_robin">Todos contra todos</option>
                      <option value="single_elimination">Eliminacion directa</option>
                    </Form.Select>
                  </Col>
                  <Col>
                    <Form.Control
                      type="number"
                      min="1"
                      placeholder="Max. rondas"
                      value={tournamentForm.maxRounds}
                      onChange={event => setTournamentForm({ ...tournamentForm, maxRounds: event.target.value })}
                    />
                  </Col>
                </Row>
                <div className="d-flex align-items-center justify-content-between gap-2">
                  <small className="muted-copy">
                    Recomendacion actual: {recommendedRounds(tournamentForm.format, tournamentForm.capacity)} rondas.
                  </small>
                  <Button
                    type="button"
                    size="sm"
                    variant="outline-dark"
                    onClick={() => setTournamentForm({
                      ...tournamentForm,
                      maxRounds: String(recommendedRounds(tournamentForm.format, tournamentForm.capacity))
                    })}
                  >
                    Usar recomendacion
                  </Button>
                </div>
                <Row className="g-2">
                  <Col>
                    <Form.Control
                      type="number"
                      min="0"
                      placeholder="Cupo"
                      value={tournamentForm.capacity}
                      onChange={event => {
                        const capacity = event.target.value;
                        setTournamentForm({
                          ...tournamentForm,
                          capacity,
                          maxRounds: String(recommendedRounds(tournamentForm.format, capacity))
                        });
                      }}
                    />
                  </Col>
                  <Col>
                    <Form.Control
                      type="number"
                      min="0"
                      step="0.01"
                      placeholder="Entrada"
                      value={tournamentForm.entryFee}
                      onChange={event => setTournamentForm({ ...tournamentForm, entryFee: event.target.value })}
                    />
                  </Col>
                </Row>
                <Row className="g-2">
                  <Col>
                    <Form.Control
                      type="number"
                      min="10"
                      max="180"
                      placeholder="Duracion ronda (min)"
                      value={tournamentForm.roundDuration}
                      onChange={event => setTournamentForm({ ...tournamentForm, roundDuration: event.target.value })}
                    />
                  </Col>
                  <Col>
                    <Form.Control
                      type="number"
                      min="1"
                      placeholder="Mesas disponibles"
                      value={tournamentForm.tableCount}
                      onChange={event => setTournamentForm({ ...tournamentForm, tableCount: event.target.value })}
                    />
                  </Col>
                  <Col>
                    <Form.Select
                      value={tournamentForm.bestOf}
                      onChange={event => setTournamentForm({ ...tournamentForm, bestOf: event.target.value })}
                    >
                      <option value="1">Mejor de 1</option>
                      <option value="3">Mejor de 3</option>
                      <option value="5">Mejor de 5</option>
                    </Form.Select>
                  </Col>
                  <Col>
                    <Form.Select
                      value={tournamentForm.status}
                      onChange={event => setTournamentForm({ ...tournamentForm, status: event.target.value })}
                    >
                      <option value="scheduled">Programado</option>
                      <option value="registration_open">Asistencia abierta</option>
                      <option value="live">En vivo</option>
                      <option value="completed">Finalizado</option>
                      <option value="cancelled">Cancelado</option>
                      <option value="draft">Borrador</option>
                    </Form.Select>
                  </Col>
                </Row>
                <Form.Control
                  placeholder="Ranking oficial URL"
                  value={tournamentForm.externalRankingUrl}
                  onChange={event => setTournamentForm({ ...tournamentForm, externalRankingUrl: event.target.value })}
                />
                <Form.Control
                  as="textarea"
                  rows={3}
                  placeholder="Descripcion"
                  value={tournamentForm.description}
                  onChange={event => setTournamentForm({ ...tournamentForm, description: event.target.value })}
                />
                {editingTournamentId && (
                  <div className="tournament-update-notify">
                    <Form.Check
                      type="switch"
                      label="Notificar cambios a comunidad e inscritos"
                      checked={Boolean(tournamentForm.notifyChange)}
                      onChange={event => setTournamentForm({ ...tournamentForm, notifyChange: event.target.checked })}
                    />
                    {tournamentForm.notifyChange && (
                      <Form.Control
                        as="textarea"
                        rows={2}
                        placeholder="Mensaje para el aviso, por ejemplo: Cambiamos el horario a las 18:30."
                        value={tournamentForm.changeMessage}
                        onChange={event => setTournamentForm({ ...tournamentForm, changeMessage: event.target.value })}
                      />
                    )}
                  </div>
                )}
                <Button type="submit" className="btn-cardora">
                  {editingTournamentId ? "Guardar cambios" : "Publicar evento"}
                </Button>
              </Form>
            </Card.Body>
          </Card>
        </Col>}
        {activeAdminSection === "tournaments" && <Col lg={5}>
          <Card className="cardora-card h-100">
            <Card.Body>
              <Card.Title>Eventos publicados</Card.Title>
              <div className="admin-list">
                {hub.tournaments?.length === 0 && <div className="muted-copy">Todavia no hay eventos.</div>}
                {hub.tournaments?.map(tournament => (
                  <div
                    className={`admin-resource-row admin-resource-selectable ${selectedTournamentId === tournament.id ? "is-active" : ""}`}
                    key={tournament.id}
                  >
                    <button type="button" className="admin-resource-main" onClick={() => setSelectedTournamentId(tournament.id)}>
                      <strong>{tournament.name}</strong>
                      <span>
                        {tournament.players_count || 0}/{tournament.capacity || "sin limite"} jugadores
                        {tournament.starts_at ? ` · ${new Date(tournament.starts_at).toLocaleString("es-ES")}` : ""}
                      </span>
                    </button>
                    <div className="admin-resource-actions">
                      <Badge bg="light" text="dark">{tournament.status}</Badge>
                      <Button size="sm" variant="outline-dark" onClick={() => editTournament(tournament)}>
                        Editar
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </Card.Body>
          </Card>
        </Col>}

        {activeAdminSection === "communications" && <Col lg={7}>
          <Card className="cardora-card h-100">
            <Card.Body>
              <Card.Title>Comunicado</Card.Title>
              <Form className="admin-form-grid" onSubmit={submitCommunication}>
                <Form.Control
                  as="textarea"
                  rows={8}
                  placeholder="Mensaje"
                  value={communicationBody}
                  onChange={event => setCommunicationBody(event.target.value)}
                  required
                />
                <Button type="submit" className="btn-cardora">
                  Publicar comunicado
                </Button>
              </Form>
            </Card.Body>
          </Card>
        </Col>}
        {activeAdminSection === "communications" && <Col lg={5}>
          <Card className="cardora-card h-100">
            <Card.Body>
              <Card.Title>Comunicados recientes</Card.Title>
              <div className="admin-list">
                {hub.posts?.length === 0 && <div className="muted-copy">Todavia no hay comunicados.</div>}
                {hub.posts?.slice(0, 6).map(post => (
                  <div className="admin-resource-row" key={post.id}>
                    <div>
                      <strong>{post.type}</strong>
                      <span>{post.body}</span>
                    </div>
                  </div>
                ))}
              </div>
            </Card.Body>
          </Card>
        </Col>}
      </Row>

      {activeAdminSection === "metrics" && <Row className="g-3 mt-1">
        <Col lg={4}>
          <Card className="cardora-card h-100">
            <Card.Body>
              <Card.Title>Catalogo</Card.Title>
              <Card.Text className="muted-copy mb-0">{hub.products?.length || 0} productos en catalogo.</Card.Text>
            </Card.Body>
          </Card>
        </Col>
        <Col lg={4}>
          <Card className="cardora-card h-100">
            <Card.Body>
              <Card.Title>Eventos</Card.Title>
              <Card.Text className="muted-copy mb-0">{hub.tournaments?.length || 0} eventos publicados.</Card.Text>
            </Card.Body>
          </Card>
        </Col>
        <Col lg={4}>
          <Card className="cardora-card h-100">
            <Card.Body>
              <Card.Title>Feed</Card.Title>
              <Card.Text className="muted-copy mb-0">{hub.posts?.length || 0} comunicados y publicaciones.</Card.Text>
            </Card.Body>
          </Card>
        </Col>
      </Row>}

      {activeAdminSection === "tournaments" && <Card className="cardora-card mt-4">
        <Card.Body>
          <div className="d-flex flex-column flex-lg-row justify-content-between gap-3 mb-3">
            <div>
              <Card.Title>Gestion de evento</Card.Title>
              <Card.Text className="muted-copy mb-0">
                Asistencias, asistencia, comunidad y oportunidades.
              </Card.Text>
            </div>
            <div className="d-flex flex-wrap gap-2">
              <Form.Select
                value={selectedTournamentId}
                onChange={event => setSelectedTournamentId(event.target.value)}
                style={{ minWidth: 240 }}
              >
                <option value="">Seleccionar evento</option>
                {hub.tournaments?.map(tournament => (
                  <option value={tournament.id} key={tournament.id}>
                    {tournament.name}
                  </option>
                ))}
              </Form.Select>
              <Button className="btn-cardora" onClick={generateRound} disabled={!tournamentControl.canGenerateRound}>
                {tournamentControl.currentRound ? `Generar ronda ${tournamentControl.nextRoundNumber}` : "Generar ronda 1"}
              </Button>
              <Button
                variant="outline-danger"
                onClick={finalizeTournament}
                disabled={!tournamentControl.canFinalize}
              >
                Finalizar evento
              </Button>
            </div>
          </div>

          {!selectedTournamentId && <div className="muted-copy">Crea o selecciona un evento para gestionarlo.</div>}

          {tournamentDetail && (
            <Row className="g-4">
              <Col xs={12}>
                <div className="tournament-admin-summary">
                  <span>Estado: {tournamentDetail.tournament.status}</span>
                  <span>Formato: {tournamentDetail.tournament.format || "swiss"}</span>
                  <span>Serie: mejor de {tournamentDetail.tournament.best_of || 3}</span>
                  <span>Ronda: {tournamentDetail.tournament.round_duration_minutes || 50} min</span>
                  <span>Mesas: {tournamentDetail.tournament.table_count || "sin limite"}</span>
                  <span>
                    Inicio ronda actual: {tournamentDetail.tournament.current_round_started_at
                      ? new Date(tournamentDetail.tournament.current_round_started_at).toLocaleTimeString("es-ES", { hour: "2-digit", minute: "2-digit" })
                      : "sin iniciar"}
                  </span>
                </div>
              </Col>
              <Col xs={12}>
                <div className="tournament-live-control">
                  <div>
                    <span className="muted-copy">Control en vivo</span>
                    <strong>{tournamentControl.nextAction}</strong>
                  </div>
                  <div className="tournament-live-control-grid">
                    <span><b>{tournamentControl.activePlayers.length}</b> activos</span>
                    <span><b>{tournamentControl.checkedInPlayers.length}</b> check-in</span>
                    <span><b>{tournamentControl.waitlistedPlayers.length}</b> espera</span>
                    <span><b>{tournamentControl.pendingMatches}</b> mesas pendientes</span>
                    <span><b>{tournamentControl.requiredTables}</b> mesas necesarias</span>
                  </div>
                  <div className="tournament-progress-track" aria-label="Progreso de ronda">
                    <span style={{ width: `${tournamentControl.progress}%` }} />
                  </div>
                  <small>
                    {tournamentControl.currentRound
                      ? `Ronda ${tournamentControl.currentRound.round_number}: ${tournamentControl.completedMatches}/${tournamentControl.totalMatches} mesas cerradas`
                      : "Sin rondas generadas todavia"}
                    {tournamentControl.maxRounds ? ` · maximo ${tournamentControl.maxRounds} rondas` : ""}
                  </small>
                </div>
              </Col>
              <Col lg={4}>
                <h2 className="admin-panel-title">Jugadores</h2>
                <Form className="feed-comment-form mb-3" onSubmit={addPlayer}>
                  <Form.Control
                    value={playerLookup}
                    onChange={event => setPlayerLookup(event.target.value)}
                    placeholder="Email o ID de usuario"
                  />
                  <Button type="submit" variant="outline-dark">
                    Inscribir
                  </Button>
                </Form>
                <div className="admin-list">
                  {tournamentDetail.players.length === 0 && <div className="muted-copy">Sin asistencias.</div>}
                  {tournamentDetail.players.map(player => (
                    <div className="admin-player-row" key={player.user_id}>
                      <div>
                        <strong>{player.display_name}</strong>
                        <span>
                          {player.status === "waitlisted"
                            ? `Posicion ${player.waitlist_position || "-"}`
                            : `${player.points} pts - ${player.wins}V ${player.losses}D ${player.draws}E`}
                        </span>
                      </div>
                      <div className="admin-player-actions">
                        <Badge bg={player.status === "dropped" ? "secondary" : player.status === "checked_in" ? "success" : "warning"} text={player.status === "registered" ? "dark" : undefined}>
                          {playerStatusLabels[player.status] || player.status}
                        </Badge>
                        {player.status === "registered" && (
                          <Button size="sm" variant="outline-success" onClick={() => markPlayerStatus(player.user_id, "checked_in")}>
                            Confirmar
                          </Button>
                        )}
                        {player.status === "waitlisted" && (
                          <Button size="sm" variant="outline-success" onClick={() => markPlayerStatus(player.user_id, "registered")}>
                            Dar cupo
                          </Button>
                        )}
                        {player.status === "checked_in" && (
                          <Button size="sm" variant="outline-dark" onClick={() => markPlayerStatus(player.user_id, "registered")}>
                            Quitar confirmacion
                          </Button>
                        )}
                        {player.status !== "dropped" ? (
                          <Button size="sm" variant="outline-danger" onClick={() => markPlayerStatus(player.user_id, "dropped")}>
                            Dar de baja
                          </Button>
                        ) : (
                          <Button size="sm" variant="outline-secondary" onClick={() => markPlayerStatus(player.user_id, "registered")}>
                            Reactivar
                          </Button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </Col>

              <Col xs={12}>
                <div className="tournament-rounds-toolbar">
                  <h2 className="admin-panel-title">Vista de rondas</h2>
                  <Button
                    type="button"
                    variant="outline-dark"
                    size="sm"
                    disabled={!selectedTournamentId}
                    onClick={() => loadTournamentDetail(selectedTournamentId)}
                  >
                    Actualizar
                  </Button>
                </div>
                <div className="tournament-bracket-board">
                  {tournamentDetail.rounds.length === 0 && (
                    <div className="muted-copy">Todavia no hay rondas para visualizar.</div>
                  )}
                  {tournamentDetail.rounds.map(round => (
                    <section className="tournament-bracket-round" key={round.id}>
                      <div className="tournament-bracket-round-head">
                        <strong>Ronda {round.round_number}</strong>
                        <Badge bg={round.status === "completed" ? "success" : "warning"} text={round.status === "active" ? "dark" : undefined}>
                          {round.status}
                        </Badge>
                      </div>
                      <div className="tournament-bracket-matches">
                        {round.matches.map(match => (
                          <div className={`tournament-bracket-match ${match.status === "completed" ? "is-complete" : ""}`} key={match.id}>
                            <span>Mesa {match.table_number}</span>
                            <div className={match.winner_id === match.player_one_id ? "is-winner" : ""}>
                              {match.player_one_name}
                              <b>{match.player_one_score ?? 0}</b>
                            </div>
                            <div className={match.winner_id === match.player_two_id ? "is-winner" : ""}>
                              {match.player_two_name || "BYE"}
                              <b>{match.player_two_score ?? 0}</b>
                            </div>
                          </div>
                        ))}
                      </div>
                    </section>
                  ))}
                </div>
              </Col>

              <Col lg={8}>
                <h2 className="admin-panel-title">Rondas</h2>
                <div className="admin-list">
                  {tournamentDetail.rounds.length === 0 && (
                    <div className="muted-copy">Todavia no hay rondas generadas.</div>
                  )}
                  {tournamentDetail.rounds.map(round => (
                    <div className="admin-round" key={round.id}>
                      <div className="d-flex justify-content-between gap-3 mb-2">
                        <strong>Ronda {round.round_number}</strong>
                        <Badge bg={round.status === "completed" ? "success" : "warning"} text={round.status === "active" ? "dark" : undefined}>
                          {round.status}
                        </Badge>
                      </div>
                      <div className="admin-list">
                        {round.matches.map(match => (
                          <div className="admin-match-row" key={match.id}>
                            <span>Mesa {match.table_number}</span>
                            <strong>
                              {match.player_one_name} vs {match.player_two_name || "BYE"}
                            </strong>
                            <span className="muted-copy">
                              {match.status === "completed"
                                ? `Resultado: ${match.winner_name || "Empate"} (${match.player_one_score ?? 0}-${match.player_two_score ?? 0})`
                                : match.status === "reported" ? `Resultado pendiente · ${match.player_one_score || 0}-${match.player_two_score || 0}` : match.status === "disputed" ? "Resultado en disputa" : match.started_at ? `En juego · ${match.player_one_score || 0}-${match.player_two_score || 0}` : "Esperando jugadores"}
                            </span>
                            {match.games?.length > 0 && (
                              <div className="d-flex flex-wrap gap-1 admin-match-history">
                                {match.games.map(game => (
                                  <Badge bg="light" text="dark" key={game.id}>
                                    P{game.game_number}: {game.winner_name}
                                  </Badge>
                                ))}
                              </div>
                            )}
                            <div className="tournament-admin-series">
                              {match.player_two_id && (
                                <div className="tournament-admin-game-slots">
                                  {Array.from({ length: Number(tournamentDetail?.tournament?.best_of || 3) }, (_, index) => {
                                    const game = match.games?.[index];
                                    return (
                                      <span className={game ? "is-complete" : ""} key={index}>
                                        Partida {index + 1}
                                        <small>{game?.winner_name || "Pendiente"}</small>
                                      </span>
                                    );
                                  })}
                                </div>
                              )}
                              <div className="d-flex flex-wrap gap-1">
                              {match.player_two_id && match.status === "pending" ? (
                                <>
                                  <Button size="sm" variant="dark" disabled={pendingMatchAction === match.id} onClick={() => recordGame(match, match.player_one_id)}>
                                    Partida {(match.games?.length || 0) + 1}: gana {match.player_one_name}
                                  </Button>
                                  <Button size="sm" variant="dark" disabled={pendingMatchAction === match.id} onClick={() => recordGame(match, match.player_two_id)}>
                                    Partida {(match.games?.length || 0) + 1}: gana {match.player_two_name}
                                  </Button>
                                </>
                              ) : !match.player_two_id && match.status !== "completed" ? (
                                <Button size="sm" variant="outline-dark" onClick={() => recordResult(match, "bye")}>
                                  Confirmar BYE
                                </Button>
                              ) : null}
                                {(match.games?.length || 0) > 0 && (
                                  <Button size="sm" variant="outline-danger" disabled={pendingMatchAction === match.id} onClick={() => undoLastGame(match)}>
                                    Corregir ultima partida
                                  </Button>
                                )}
                                {match.player_two_id && ["reported", "disputed"].includes(match.status) && (
                                  <>
                                    <Button size="sm" variant="outline-dark" disabled={pendingMatchAction === match.id} onClick={() => recordResult(match, "player_one_win", true)}>Resolver para {match.player_one_name}</Button>
                                    <Button size="sm" variant="outline-dark" disabled={pendingMatchAction === match.id} onClick={() => recordResult(match, "player_two_win", true)}>Resolver para {match.player_two_name}</Button>
                                  </>
                                )}
                              </div>
                              {match.player_two_id && match.status === "pending" && tournamentDetail?.tournament?.format !== "single_elimination" && (
                                <details>
                                  <summary className="muted-copy">Cierre manual</summary>
                                  <Button className="mt-2" size="sm" variant="outline-secondary" onClick={() => recordResult(match, "draw")}>
                                    Cerrar como empate
                                  </Button>
                                </details>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </Col>
            </Row>
          )}
        </Card.Body>
      </Card>}
    </Container>
  );
}

export default AdminLayout;



